import { __assign, __rest } from 'tslib';
import { alpha, lighten, darken, createTheme } from '@mui/material/styles';
import { dividerClasses } from '@mui/material/Divider';
import { paperClasses } from '@mui/material/Paper';
import { g as getPopoverPlacement } from '../getPopoverPlacement-19aa073e.js';
import deepmerge from 'deepmerge';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { alertClasses } from '@mui/material/Alert';
import { buttonClasses } from '@mui/material/Button';
import { buttonGroupClasses } from '@mui/material/ButtonGroup';
import React from 'react';
import CheckBoxBlankWhiteFilled from '../icons/CheckBoxBlankWhiteFilled/index.js';
import CheckBoxIndeterminateWhiteFilled from '../icons/CheckBoxIndeterminateWhiteFilled/index.js';
import CheckBoxWhiteFilled from '../icons/CheckBoxWhiteFilled/index.js';
import { chipClasses } from '@mui/material/Chip';
import { switchClasses } from '@mui/material/Switch';
import { grey } from '@mui/material/colors';
import '../createSvgIcon-de76dc64.js';
import '@mui/material/SvgIcon';

var MuiDialog = function (theme) { return ({
    defaultProps: {
        fullWidth: true,
    },
    styleOverrides: {
        paper: {
            borderRadius: theme.shape.borderRadius,
        },
        paperFullScreen: {
            borderRadius: 0,
        },
    },
}); };
var MuiDialogTitle = function () { return ({
    styleOverrides: {
        root: function (_a) {
            var theme = _a.theme;
            return ({
                padding: theme.spacing(3),
            });
        },
    },
}); };
var MuiDialogContent = function () { return ({
    styleOverrides: {
        root: function (_a) {
            var theme = _a.theme;
            return ({
                padding: theme.spacing(0, 3, 3),
            });
        },
    },
}); };
var MuiDialogActions = function () { return ({
    styleOverrides: {
        root: function (_a) {
            var theme = _a.theme;
            return ({
                padding: theme.spacing(2, 3, 3),
            });
        },
    },
}); };

var MuiList = function (theme) { return ({
    styleOverrides: {
        root: function () {
            var paddingY = 8 - theme.componentsConfig.listItem.spacingY / 2;
            return {
                display: 'flex',
                flexDirection: 'column',
                paddingTop: paddingY,
                paddingBottom: paddingY,
            };
        },
    },
}); };
var BaseOverrideListItem = /** @class */ (function () {
    function BaseOverrideListItem(theme) {
        var _this = this;
        this.theme = theme;
        this.root = function (_a) {
            var ownerState = _a.ownerState;
            var theme = _this.theme;
            var _b = theme.componentsConfig.listItem, paddingX = _b.paddingX, spacingY = _b.spacingY, paddingY = _b.paddingY;
            var _c = theme.palette, primary = _c.primary, action = _c.action;
            return __assign(__assign({ borderRadius: theme.shape.borderRadiusLarge, marginTop: spacingY / 2, marginBottom: spacingY / 2 }, (!ownerState.disablePadding && __assign({ paddingTop: paddingY, paddingBottom: paddingY }, (!ownerState.disableGutters && {
                paddingLeft: paddingX,
                paddingRight: paddingX,
            })))), (ownerState.selected && {
                color: primary.main,
                backgroundColor: alpha(primary.main, action.selectedOpacity),
            }));
        };
        this.disabled = function (_a) {
            var ownerState = _a.ownerState;
            var theme = _this.theme;
            if (ownerState.selected) {
                return {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.action.selected,
                };
            }
            return {};
        };
        this.divider = function () {
            var theme = _this.theme;
            var _a = theme.componentsConfig, divider = _a.divider, listItem = _a.listItem;
            var marginBottom = listItem.spacingY / 2 + divider.width;
            return {
                borderBottom: 'none',
                backgroundClip: 'initial',
                position: 'relative',
                marginBottom: marginBottom,
                '&:last-child': {
                    marginBottom: marginBottom,
                },
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    height: "".concat(divider.width, "px"),
                    backgroundColor: theme.palette.divider,
                    bottom: -marginBottom,
                },
            };
        };
    }
    return BaseOverrideListItem;
}());
var MuiListItem = function (theme) {
    var _a = new BaseOverrideListItem(theme), root = _a.root, disabled = _a.disabled, divider = _a.divider;
    return {
        styleOverrides: {
            root: root,
            disabled: disabled,
            divider: divider,
        },
    };
};
var MuiListItemButton = function (theme) {
    var _a = new BaseOverrideListItem(theme), root = _a.root, disabled = _a.disabled, divider = _a.divider;
    return {
        styleOverrides: {
            root: root,
            disabled: disabled,
            divider: divider,
        },
    };
};
var MuiListItemAvatar = function (theme) {
    var _a = theme.componentsConfig, avatar = _a.avatar, listItemAvatar = _a.listItemAvatar;
    return {
        styleOverrides: {
            root: {
                minWidth: avatar.size.medium + listItemAvatar.spacing,
            },
        },
    };
};
var MuiListItemIcon = function (theme) {
    var _a = theme.componentsConfig, svgIcon = _a.svgIcon, listItemIcon = _a.listItemIcon;
    return {
        styleOverrides: {
            root: {
                minWidth: svgIcon.size.medium + listItemIcon.spacing,
            },
        },
    };
};
var MuiListItemText = function () { return ({
    styleOverrides: {
        root: {
            marginTop: 0,
            marginBottom: 0,
        },
    },
}); };

var roundTo6Decimals = function (num) { return Math.round(num * 1000000) / 1000000; };
var roundTo4Decimals = function (num) {
    return Math.round(num * 10000) / 10000;
};
var roundTo2Decimals = function (num) { return Math.round(num * 100) / 100; };
var htmlFontSize = 16;
var getSizeRem = function (px) { return roundTo2Decimals(px / htmlFontSize) + 'rem'; };
var getLineHeight = function (fontSizePx, lineHeightPx) {
    return roundTo4Decimals(lineHeightPx / fontSizePx);
};
var getLetterSpacingEm = function (fontSizePx, letterSpacingPx) {
    return roundTo6Decimals(letterSpacingPx / fontSizePx) + 'em';
};
var getFontSizes = function (fontSizePx, lineHeightPx, letterSpacingPx, fontWeight) { return ({
    fontSize: getSizeRem(fontSizePx),
    lineHeight: getLineHeight(fontSizePx, lineHeightPx),
    letterSpacing: getLetterSpacingEm(fontSizePx, letterSpacingPx),
    fontWeight: fontWeight,
}); };

var spacing = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    return args.map(function (size) { return "".concat(size, "px"); }).join(' ');
};

function defaults(defaults, options) {
    return deepmerge(defaults, options);
}

var itemSpacing = 4;
var halfItemSpacing = itemSpacing / 2;
var MuiMenu = function (theme) {
    var _a;
    var _b = theme.componentsConfig, popover = _b.popover, menu = _b.menu;
    return {
        defaultProps: __assign({ variant: 'menu' }, getPopoverPlacement(theme.direction === 'rtl' ? 'bottom-end' : 'bottom-start', popover.triggerSpacing)),
        styleOverrides: {
            paper: (_a = {},
                _a["&.".concat(paperClasses.rounded)] = {
                    borderRadius: theme.shape.borderRadius,
                },
                _a),
            list: {
                padding: spacing(menu.paddingY, menu.paddingX),
            },
        },
    };
};
var MuiMenuItem = function (theme) { return ({
    styleOverrides: {
        root: function (_a) {
            var _b;
            var ownerState = _a.ownerState;
            var _c = theme.componentsConfig, divider = _c.divider, menu = _c.menu;
            return __assign(__assign(__assign(__assign(__assign({ marginTop: halfItemSpacing, marginBottom: halfItemSpacing, paddingTop: 8, paddingBottom: 8, borderRadius: theme.shape.borderRadius }, (!ownerState.disableGutters && {
                paddingLeft: 8,
                paddingRight: 8,
            })), { overflow: 'initial' }), (ownerState.divider && {
                borderBottom: 'none',
                backgroundClip: 'initial',
                position: 'relative',
                marginBottom: halfItemSpacing + divider.width,
                '&:last-child': {
                    marginBottom: halfItemSpacing + divider.width,
                },
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    height: "".concat(divider.width, "px"),
                    backgroundColor: theme.palette.divider,
                    right: -menu.paddingX,
                    left: -menu.paddingX,
                    bottom: -(halfItemSpacing + divider.width),
                },
            })), (ownerState.selected && {
                color: theme.palette.primary.main,
            })), (_b = {}, _b["& + .".concat(dividerClasses.root)] = {
                marginTop: 0,
                marginBottom: 0,
            }, _b));
        },
    },
}); };

var MuiFormLabel = function (theme) { return ({
    styleOverrides: {
        asterisk: {
            color: theme.palette.error.main,
        },
    },
}); };
var MuiInputLabel = function (theme) { return ({
    styleOverrides: {
        root: function (_a) {
            var ownerState = _a.ownerState;
            var _b = theme.componentsConfig.outlinedInput, paddingX = _b.paddingX, paddingY = _b.paddingY;
            return __assign({}, (ownerState.variant === 'outlined' && __assign({ transform: "translate(".concat(paddingX, "px, ").concat(paddingY, "px) scale(1)") }, (ownerState.shrink && {
                transform: 'translate(14px, -9px) scale(0.75)',
            }))));
        },
    },
}); };
var MuiOutlinedInput = function (theme) {
    var _a, _b, _c;
    var _d = theme.componentsConfig.outlinedInput, paddingX = _d.paddingX, paddingY = _d.paddingY, borderWidth = _d.borderWidth;
    return {
        styleOverrides: {
            root: (_a = {
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.common.white
                },
                _a["&.".concat(outlinedInputClasses.disabled)] = {
                    backgroundColor: theme.palette.grey[300],
                },
                _a["&.".concat(outlinedInputClasses.error)] = {
                    backgroundColor: theme.palette.error.light,
                },
                _a),
            multiline: {
                padding: 0,
            },
            input: {
                padding: spacing(paddingY, paddingX),
            },
            adornedStart: (_b = {},
                _b["& .".concat(outlinedInputClasses.input)] = {
                    paddingLeft: 0,
                },
                _b),
            adornedEnd: (_c = {},
                _c["& .".concat(outlinedInputClasses.input)] = {
                    paddingRight: 0,
                },
                _c),
            notchedOutline: {
                borderWidth: borderWidth,
                padding: spacing(0, paddingX - 5),
            },
        },
    };
};
var MuiFormHelperText = function (theme) {
    var paddingX = theme.componentsConfig.outlinedInput.paddingX;
    return {
        styleOverrides: {
            contained: {
                marginLeft: paddingX,
                marginRight: paddingX,
            },
        },
    };
};
var MuiTextField = function () { return ({
    defaultProps: {
        margin: 'normal',
        fullWidth: true,
    },
}); };

var MuiAlert = function (theme) {
    var _a, _b, _c, _d;
    return ({
        styleOverrides: {
            root: {
                padding: '1px 13px 1px 10px',
                borderRadius: theme.shape.borderRadius,
            },
            icon: {
                alignSelf: 'center',
            },
            action: {
                alignSelf: 'self-start',
                marginTop: 2,
            },
            standardSuccess: (_a = {
                    color: theme.palette.success.dark,
                    borderColor: theme.palette.success.dark,
                    backgroundColor: lighten(theme.palette.success.main, 0.9)
                },
                _a["& .".concat(alertClasses.icon)] = {
                    color: theme.palette.success.dark,
                },
                _a),
            standardInfo: (_b = {
                    color: theme.palette.info.dark,
                    borderColor: theme.palette.info.dark,
                    backgroundColor: lighten(theme.palette.info.main, 0.9)
                },
                _b["& .".concat(alertClasses.icon)] = {
                    color: theme.palette.info.dark,
                },
                _b),
            standardWarning: (_c = {
                    color: theme.palette.warning.dark,
                    borderColor: theme.palette.warning.dark,
                    backgroundColor: lighten(theme.palette.warning.main, 0.9)
                },
                _c["& .".concat(alertClasses.icon)] = {
                    color: theme.palette.warning.dark,
                },
                _c),
            standardError: (_d = {
                    color: theme.palette.error.dark,
                    borderColor: theme.palette.error.dark,
                    backgroundColor: lighten(theme.palette.error.main, 0.9)
                },
                _d["& .".concat(alertClasses.icon)] = {
                    color: theme.palette.error.dark,
                },
                _d),
        },
    });
};

var MuiAppBar = function () { return ({
    defaultProps: {
        variant: 'elevation',
    },
}); };

var outlinedBorderWidth = 1;
var MuiButton = function (_a) {
    var _b;
    var _c = _a.componentsConfig.button, letterSpacing = _c.letterSpacing, fontWeight = _c.fontWeight, small = _c.small, medium = _c.medium, large = _c.large, palette = _a.palette;
    return ({
        defaultProps: {
            size: 'large',
        },
        styleOverrides: {
            root: {
                padding: spacing(medium.padding.topBottom, medium.padding.rightLeft),
            },
            text: {
                padding: spacing(medium.padding.topBottom, medium.padding.rightLeft),
            },
            outlined: (_b = {
                    padding: spacing(medium.padding.topBottom - outlinedBorderWidth, medium.padding.rightLeft - outlinedBorderWidth)
                },
                _b["&.".concat(buttonClasses.disabled)] = {
                    border: "1px solid ".concat(palette.action.disabledBackground),
                },
                _b),
            textSizeSmall: {
                padding: spacing(small.padding.topBottom, small.padding.rightLeft),
            },
            textSizeLarge: {
                padding: spacing(large.padding.topBottom, large.padding.rightLeft),
            },
            outlinedSizeSmall: {
                padding: spacing(small.padding.topBottom - outlinedBorderWidth, small.padding.rightLeft - outlinedBorderWidth),
            },
            outlinedSizeLarge: {
                padding: spacing(large.padding.topBottom - outlinedBorderWidth, large.padding.rightLeft - outlinedBorderWidth),
            },
            containedSizeSmall: {
                padding: spacing(small.padding.topBottom, small.padding.rightLeft),
            },
            containedSizeLarge: {
                padding: spacing(large.padding.topBottom, large.padding.rightLeft),
            },
            sizeSmall: getFontSizes(small.font.fontSize, small.font.lineHeight, letterSpacing, fontWeight),
            sizeLarge: getFontSizes(large.font.fontSize, large.font.lineHeight, letterSpacing, fontWeight),
        },
        variants: [
            {
                props: { variant: 'contained', color: 'grey' },
                style: {
                    color: palette.getContrastText(palette.grey[300]),
                },
            },
            {
                props: { variant: 'outlined', color: 'grey' },
                style: {
                    color: palette.text.primary,
                    borderColor: palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.23)'
                        : 'rgba(255, 255, 255, 0.23)',
                    '&:hover': {
                        borderColor: palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.23)'
                            : 'rgba(255, 255, 255, 0.23)',
                        backgroundColor: alpha(palette.text.primary, palette.action.hoverOpacity),
                    },
                },
            },
            {
                props: { color: 'grey', variant: 'text' },
                style: {
                    color: palette.text.primary,
                    '&:hover': {
                        backgroundColor: alpha(palette.text.primary, palette.action.hoverOpacity),
                    },
                },
            },
        ],
    });
};

var MuiButtonGroup = function () {
    var _a, _b;
    return ({
        variants: [
            {
                props: {
                    color: 'grey',
                    variant: 'outlined',
                    orientation: 'horizontal',
                },
                style: (_a = {},
                    _a["& .".concat(buttonGroupClasses.grouped)] = {
                        '&:not(:last-of-type)': {
                            '&:hover': {
                                borderRightColor: 'transparent',
                            },
                        },
                    },
                    _a),
            },
            {
                props: { color: 'grey', variant: 'outlined', orientation: 'vertical' },
                style: (_b = {},
                    _b["& .".concat(buttonGroupClasses.grouped)] = {
                        '&:not(:last-of-type)': {
                            '&:hover': {
                                borderBottomColor: 'transparent',
                            },
                        },
                    },
                    _b),
            },
        ],
    });
};

var MuiCheckbox = function (theme) { return ({
    defaultProps: {
        checkedIcon: React.createElement(CheckBoxWhiteFilled, null),
        icon: React.createElement(CheckBoxBlankWhiteFilled, null),
        indeterminateIcon: React.createElement(CheckBoxIndeterminateWhiteFilled, null),
    },
    styleOverrides: {
        root: {
            padding: theme.componentsConfig.checkbox.padding,
        },
    },
}); };

var MuiChip = function (_a) {
    var palette = _a.palette, componentsConfig = _a.componentsConfig;
    return ({
        defaultProps: {
            size: 'small',
        },
        styleOverrides: {
            root: function (_a) {
                var _b;
                var _c = _a.ownerState, color = _c.color, size = _c.size, variant = _c.variant;
                return (__assign(__assign((_b = {}, _b["& .".concat(chipClasses.avatar)] = __assign(__assign(__assign({}, (color === 'primary' && __assign({ color: palette.primary.main, backgroundColor: palette.primary.semiLight }, (variant === 'outlined' && {
                    color: palette.common.white,
                    backgroundColor: palette.primary.main,
                })))), ((color === 'secondary' ||
                    color === 'error' ||
                    color === 'info') && __assign({ color: palette.common.white, backgroundColor: palette[color].semiLight }, (variant === 'outlined' && {
                    color: palette.common.white,
                    backgroundColor: palette[color].main,
                })))), ((color === 'default' ||
                    color === 'success' ||
                    color === 'warning') && {
                    backgroundColor: palette.grey[componentsConfig.avatar.bgColor],
                    color: palette.common.white,
                })), _b["& .".concat(chipClasses.avatarSmall)] = __assign({ marginLeft: 2, width: 16, height: 16 }, (variant === 'outlined' && {
                    marginLeft: 1,
                })), _b["& .".concat(chipClasses.icon)] = __assign({}, (size === 'small' && __assign({ fontSize: 16, marginLeft: 2 }, (variant === 'outlined' && {
                    marginLeft: 1,
                })))), _b["& .".concat(chipClasses.deleteIcon)] = __assign({}, (size === 'small' && __assign({ marginRight: 2 }, (variant === 'outlined' && {
                    marginRight: 1,
                })))), _b), (size === 'small' && {
                    height: 20,
                })), ((color === 'success' || color === 'warning') &&
                    variant === 'outlined' && {
                    color: palette.warning.contrastText,
                })));
            },
        },
    });
};

var MuiFormControl = function (theme) { return ({
    defaultProps: {
        margin: 'normal',
        fullWidth: true,
    },
    styleOverrides: theme.componentsConfig.formControl,
}); };

var defaultSize = 'medium';
var MuiIconButton = function (_a) {
    var iconButton = _a.componentsConfig.iconButton, typography = _a.typography;
    return ({
        styleOverrides: {
            root: function (_a) {
                var _b = _a.ownerState.size, size = _b === void 0 ? defaultSize : _b;
                return ({
                    fontSize: typography.pxToRem(16),
                    padding: iconButton[size].padding,
                });
            },
            edgeStart: function (_a) {
                var _b = _a.ownerState.size, size = _b === void 0 ? defaultSize : _b;
                return ({
                    marginLeft: -iconButton[size].padding,
                });
            },
            edgeEnd: function (_a) {
                var _b = _a.ownerState.size, size = _b === void 0 ? defaultSize : _b;
                return ({
                    marginRight: -iconButton[size].padding,
                });
            },
        },
    });
};

var MuiLink = function () { return ({
    styleOverrides: {
        button: {
            display: 'inline',
            verticalAlign: 'initial',
        },
    },
}); };

var MuiPaper = function (theme) { return ({
    defaultProps: {
        variant: 'outlined',
    },
    styleOverrides: {
        rounded: {
            borderRadius: theme.shape.borderRadiusLarge,
        },
    },
}); };

var MuiPopover = function (theme) { return ({
    defaultProps: __assign({}, getPopoverPlacement('bottom', theme.componentsConfig.popover.triggerSpacing)),
}); };

var MuiRadio = function (theme) { return ({
    defaultProps: {
        color: 'primary',
    },
    styleOverrides: {
        root: {
            padding: theme.componentsConfig.radio.padding,
        },
    },
}); };

var MuiSvgIcon = function (theme) {
    var size = theme.componentsConfig.svgIcon.size;
    return {
        styleOverrides: {
            root: function (_a) {
                var ownerState = _a.ownerState;
                return (__assign({}, (ownerState.fontSize &&
                    ownerState.fontSize !== 'inherit' && {
                    fontSize: theme.typography.pxToRem(size[ownerState.fontSize]),
                })));
            },
        },
    };
};

var getRootPadding = function (sizes) { return ({
    y: (sizes.offsetSize.height - sizes.clientSize.height) / 2,
    x: (sizes.offsetSize.width - sizes.clientSize.width) / 2,
}); };
var getSwitchBasePadding = function (sizes) { return ({
    y: (sizes.offsetSize.height - sizes.thumbSize) / 2,
    x: getRootPadding(sizes).x + (sizes.clientSize.height - sizes.thumbSize) / 2,
}); };
var getSwitchBaseTranslate = function (sizes) {
    return sizes.offsetSize.width -
        (getSwitchBasePadding(sizes).x * 2 + sizes.thumbSize);
};
var MuiSwitch = function (theme) {
    var _a, _b, _c, _d, _e;
    var _f = theme.componentsConfig.switch, mediumSizes = _f.medium, smallSizes = _f.small;
    var rootPadding = getRootPadding(mediumSizes);
    var switchBasePadding = getSwitchBasePadding(mediumSizes);
    var switchBaseTranslate = getSwitchBaseTranslate(mediumSizes);
    var rootPaddingSmall = getRootPadding(smallSizes);
    var switchBasePaddingSmall = getSwitchBasePadding(smallSizes);
    var switchBaseTranslateSmall = getSwitchBaseTranslate(smallSizes);
    return {
        defaultProps: {
            color: 'primary',
        },
        styleOverrides: {
            root: {
                width: mediumSizes.offsetSize.width,
                height: mediumSizes.offsetSize.height,
                padding: spacing(rootPadding.y, rootPadding.x),
            },
            switchBase: (_a = {
                    padding: spacing(switchBasePadding.y, switchBasePadding.x)
                },
                _a["&.".concat(switchClasses.checked)] = {
                    transform: "translateX(".concat(switchBaseTranslate, "px)"),
                },
                _a),
            track: {
                borderRadius: 9999,
            },
            thumb: {
                width: mediumSizes.thumbSize,
                height: mediumSizes.thumbSize,
                boxShadow: 'none',
            },
            colorPrimary: (_b = {},
                _b["&.".concat(switchClasses.checked)] = {
                    color: theme.palette.common.white,
                },
                _b["&.".concat(switchClasses.checked, " + .").concat(switchClasses.track)] = {
                    opacity: 1,
                },
                _b["&.".concat(switchClasses.disabled, " + .").concat(switchClasses.track)] = {
                    opacity: 0.12,
                },
                _b),
            colorSecondary: (_c = {},
                _c["&.".concat(switchClasses.checked)] = {
                    color: theme.palette.common.white,
                },
                _c["&.".concat(switchClasses.checked, " + .").concat(switchClasses.track)] = {
                    opacity: 1,
                },
                _c["&.".concat(switchClasses.disabled, " + .").concat(switchClasses.track)] = {
                    opacity: 0.12,
                },
                _c),
            sizeSmall: (_d = {
                    width: smallSizes.offsetSize.width,
                    height: smallSizes.offsetSize.height,
                    padding: spacing(rootPaddingSmall.y, rootPaddingSmall.x)
                },
                _d["& .".concat(switchClasses.thumb)] = {
                    width: smallSizes.thumbSize,
                    height: smallSizes.thumbSize,
                },
                _d["& .".concat(switchClasses.switchBase)] = (_e = {
                        padding: spacing(switchBasePaddingSmall.y, switchBasePaddingSmall.x)
                    },
                    _e["&.".concat(switchClasses.checked)] = {
                        transform: "translateX(".concat(switchBaseTranslateSmall, "px)"),
                    },
                    _e),
                _d),
        },
    };
};

var MuiFormControlLabel = function () { return ({
    styleOverrides: {
        root: {
            marginLeft: 0,
        },
        labelPlacementStart: {
            marginRight: 0,
        },
    },
}); };

var componentOverrides = /*#__PURE__*/Object.freeze({
    __proto__: null,
    MuiDialog: MuiDialog,
    MuiDialogTitle: MuiDialogTitle,
    MuiDialogContent: MuiDialogContent,
    MuiDialogActions: MuiDialogActions,
    MuiList: MuiList,
    MuiListItem: MuiListItem,
    MuiListItemButton: MuiListItemButton,
    MuiListItemAvatar: MuiListItemAvatar,
    MuiListItemIcon: MuiListItemIcon,
    MuiListItemText: MuiListItemText,
    MuiMenu: MuiMenu,
    MuiMenuItem: MuiMenuItem,
    MuiFormLabel: MuiFormLabel,
    MuiInputLabel: MuiInputLabel,
    MuiOutlinedInput: MuiOutlinedInput,
    MuiFormHelperText: MuiFormHelperText,
    MuiTextField: MuiTextField,
    MuiAlert: MuiAlert,
    MuiAppBar: MuiAppBar,
    MuiButton: MuiButton,
    MuiButtonGroup: MuiButtonGroup,
    MuiCheckbox: MuiCheckbox,
    MuiChip: MuiChip,
    MuiFormControl: MuiFormControl,
    MuiIconButton: MuiIconButton,
    MuiLink: MuiLink,
    MuiPaper: MuiPaper,
    MuiPopover: MuiPopover,
    MuiRadio: MuiRadio,
    MuiSvgIcon: MuiSvgIcon,
    MuiSwitch: MuiSwitch,
    MuiFormControlLabel: MuiFormControlLabel
});

var makeComponents = function (theme) {
    return Object.entries(componentOverrides).reduce(function (accumulator, _a) {
        var componentName = _a[0], overridesFn = _a[1];
        accumulator[componentName] = overridesFn(theme);
        return accumulator;
    }, {});
};

var defaultComponentsConfig = {
    button: {
        letterSpacing: 0,
        fontWeight: 400,
        small: {
            font: {
                fontSize: 14,
                lineHeight: 24,
            },
            padding: {
                topBottom: 4,
                rightLeft: 16,
            },
        },
        medium: {
            font: {
                fontSize: 16,
                lineHeight: 24,
            },
            padding: {
                topBottom: 8,
                rightLeft: 16,
            },
        },
        large: {
            font: {
                fontSize: 16,
                lineHeight: 24,
            },
            padding: {
                topBottom: 12,
                rightLeft: 24,
            },
        },
    },
    iconButton: {
        small: {
            padding: 4,
        },
        medium: {
            padding: 8,
        },
        large: {
            padding: 12,
        },
    },
    listItem: {
        paddingX: 8,
        paddingY: 8,
        spacingY: 2,
    },
    listItemAvatar: {
        spacing: 8,
    },
    listItemIcon: {
        spacing: 8,
    },
    avatar: {
        bgColor: 500,
        size: {
            large: 40,
            medium: 32,
            small: 24,
        },
    },
    svgIcon: {
        size: {
            small: 20,
            medium: 24,
            large: 35,
        },
    },
    popover: {
        triggerSpacing: 8,
    },
    menu: {
        paddingX: 8,
        paddingY: 8,
    },
    divider: {
        width: 1,
    },
    formControl: {
        marginNormal: {
            marginTop: 16,
            marginBottom: 8,
        },
        marginDense: {
            marginTop: 8,
            marginBottom: 4,
        },
    },
    outlinedInput: {
        borderWidth: 1,
        paddingX: 12,
        paddingY: 10.5,
    },
    switch: {
        medium: {
            offsetSize: {
                width: 58,
                height: 38,
            },
            clientSize: {
                width: 48,
                height: 24,
            },
            thumbSize: 18,
        },
        small: {
            offsetSize: {
                width: 40,
                height: 24,
            },
            clientSize: {
                width: 26,
                height: 14,
            },
            thumbSize: 10,
        },
    },
    checkbox: {
        padding: 9,
    },
    radio: {
        padding: 9,
    },
};

var defaultTonalOffset = 0.2;
var tonalOffsetMultiplier = {
    dark: 1.5,
    semiLight: 0.5,
    light: 1,
};
function getFullTonalOffset(offset) {
    if (offset === void 0) { offset = defaultTonalOffset; }
    if (typeof offset !== 'number') {
        return offset;
    }
    return {
        dark: offset * tonalOffsetMultiplier.dark,
        semiLight: offset * tonalOffsetMultiplier.semiLight,
        light: offset * tonalOffsetMultiplier.light,
    };
}

function extendSimplePaletteOption(_a, tonalOffset) {
    var main = _a.main, semiLight = _a.semiLight, light = _a.light, dark = _a.dark, rest = __rest(_a, ["main", "semiLight", "light", "dark"]);
    return __assign(__assign({}, rest), { main: main, dark: dark || darken(main, tonalOffset.dark), semiLight: semiLight || lighten(main, tonalOffset.semiLight), light: light || lighten(main, tonalOffset.light) });
}

function extendSimplePaletteOptions(options) {
    var tonalOffset = getFullTonalOffset(options.tonalOffset);
    return (Object.entries(options)
        /**
         * filter simple palette options
         * */
        .filter(function (colorEntry) {
        var paletteColor = colorEntry[1];
        return !!paletteColor.main;
    })
        /**
         * extend with dark, semiLight, light if not provided
         * */
        .map(function (_a) {
        var colorName = _a[0], colorValue = _a[1];
        return [
            colorName,
            extendSimplePaletteOption(colorValue, tonalOffset),
        ];
    })
        /**
         * compose with provided options
         * */
        .reduce(function (accumulator, _a) {
        var colorName = _a[0], colorValue = _a[1];
        accumulator[colorName] = colorValue;
        return accumulator;
    }, __assign({}, options)));
}

var black = '#212121';
var white = '#FFF';
var tonalOffset = getFullTonalOffset();
var oxygenPalette = {
    blue: {
        B110: '#0062A3',
        B100: '#0077C8',
        B80: '#C2E7FF',
        B10: '#EBF7FF',
    },
    green: {
        g100: '#26D07C',
        g80: '#96E9B8',
        g10: '#DDF9EB',
    },
    red: {
        R100: '#FF585D',
        R80: '#FF999C',
        R10: '#FFEBEB',
    },
    yellow: {
        y100: '#F4DA40',
        y80: '#F9EC9F',
        y10: '#FDF7D8',
    },
    neutrals: {
        N100: '#212529',
        N90: '#343A40',
        N80: '#5C6670',
        N70: '#9AA3AC',
        N60: '#BCC2C8',
        N50: '#DDE0E3',
        N10: '#F4F5F6',
    },
};
var breatheAppGreen = '#36A004';
var defaultPalette = {
    common: {
        black: black,
        white: white,
    },
    primary: {
        dark: oxygenPalette.blue.B110,
        main: oxygenPalette.blue.B100,
        semiLight: oxygenPalette.blue.B80,
        light: oxygenPalette.blue.B10,
    },
    secondary: {
        main: breatheAppGreen,
        semiLight: lighten(breatheAppGreen, 0.3),
        light: lighten(breatheAppGreen, 0.6),
    },
    error: {
        main: oxygenPalette.red.R100,
        semiLight: oxygenPalette.red.R80,
        light: oxygenPalette.red.R10,
    },
    warning: {
        main: oxygenPalette.yellow.y100,
        semiLight: oxygenPalette.yellow.y80,
        light: oxygenPalette.yellow.y10,
    },
    info: extendSimplePaletteOption({
        main: '#5262EF', // from styleguide
    }, tonalOffset),
    success: {
        main: oxygenPalette.green.g100,
        semiLight: oxygenPalette.green.g80,
        light: oxygenPalette.green.g10,
    },
    grey: {
        main: grey[300],
        dark: grey[400],
    },
    text: {
        primary: oxygenPalette.neutrals.N100,
        secondary: oxygenPalette.neutrals.N80,
    },
    background: {
        paper: white,
        default: '#EDF0F5', // from RTA
    },
};

var defaultShape = {
    borderRadius: 6,
    borderRadiusLarge: 12,
};

var fontFamily = [
    "'Rubik'",
    '-apple-system',
    'BlinkMacSystemFont',
    "'Segoe UI'",
    'Roboto',
    'Oxygen-Sans',
    'Ubuntu',
    'Cantarell',
    "'Helvetica Neue'",
    'Helvetica',
    'Arial',
    'sans-serif',
    "'Apple Color Emoji'",
    "'Segoe UI Emoji'",
    "'Segoe UI Symbol'",
].join(', ');
function makeTypography(componentsConfig) {
    return {
        fontFamily: fontFamily,
        htmlFontSize: htmlFontSize,
        h1: getFontSizes(36, 40, -0.75, 500),
        h2: getFontSizes(28, 36, -0.3, 500),
        h3: getFontSizes(24, 32, -0.3, 500),
        h4: getFontSizes(20, 24, 0, 500),
        h5: getFontSizes(18, 24, 0, 500),
        h6: getFontSizes(16, 24, 0, 500),
        // there is no subtitle1 in the oxygen styleguide
        subtitle1: getFontSizes(16, 24, 0, 400),
        // there is no subtitle2 in the oxygen styleguide
        subtitle2: getFontSizes(14, 24, 0, 500),
        body1: getFontSizes(16, 24, 0, 400),
        body2: getFontSizes(14, 24, 0, 400),
        button: __assign(__assign({}, getFontSizes(componentsConfig.button.medium.font.fontSize, componentsConfig.button.medium.font.lineHeight, componentsConfig.button.letterSpacing, componentsConfig.button.fontWeight)), { textTransform: 'none' }),
        caption: getFontSizes(12, 14, 0.5, 400),
    };
}

function createOxygenTheme(options) {
    if (options === void 0) { options = {}; }
    var _a = options.componentsConfig, optionsComponentsConfig = _a === void 0 ? {} : _a, _b = options.shape, optionsShape = _b === void 0 ? {} : _b, _c = options.palette, optionsPalette = _c === void 0 ? {} : _c;
    var componentsConfig = defaults(defaultComponentsConfig, optionsComponentsConfig);
    var shape = defaults(defaultShape, optionsShape);
    var palette = defaults(defaultPalette, extendSimplePaletteOptions(optionsPalette));
    var theme = createTheme(__assign(__assign({}, options), { componentsConfig: componentsConfig, shape: shape, palette: palette, typography: makeTypography(componentsConfig) }));
    theme.components = makeComponents(theme);
    return theme;
}

export { createOxygenTheme as default };
